import React, { useLayoutEffect, useRef, useState } from 'react';

// Height a long message is clipped to while collapsed, and how far past it
// the content has to run before collapsing is worth it — so "Show more"
// never exists just to reveal one or two more lines.
const COLLAPSED_MAX_HEIGHT_PX = 320;
const COLLAPSE_SLACK_PX = 64;

interface CollapsibleTextProps {
  children: React.ReactNode;
}

// Clips long content to a fixed height with a "Show more" / "Show less"
// toggle. Decides by rendered height rather than character count, so a
// message of many short lines collapses too, and it re-measures whenever
// the bubble's width changes (window resize, doc preview panel opening).
const CollapsibleText: React.FC<CollapsibleTextProps> = ({ children }) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [isLong, setIsLong] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useLayoutEffect(() => {
    const body = bodyRef.current;
    if (!body) return;

    // scrollHeight is the full content height even while clipped, so this
    // reads the same whether or not the collapsed max-height is applied.
    const measure = () => setIsLong(body.scrollHeight > COLLAPSED_MAX_HEIGHT_PX + COLLAPSE_SLACK_PX);
    measure();

    // Deferred a frame: measuring synchronously inside the observer can
    // resize the observed element in the same frame, which trips the
    // browser's "ResizeObserver loop" error.
    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    });
    observer.observe(body);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  const collapsed = isLong && !expanded;

  const toggle = () => {
    if (!expanded) {
      setExpanded(true);
      return;
    }
    setExpanded(false);
    // Collapsing can drop thousands of pixels out from under the reader —
    // bring the message back into view instead of leaving them far below it.
    requestAnimationFrame(() => rootRef.current?.scrollIntoView({ block: 'nearest' }));
  };

  return (
    <div ref={rootRef} className={`collapsible-text ${collapsed ? 'collapsed' : ''}`}>
      <div
        ref={bodyRef}
        className="collapsible-text-body"
        style={collapsed ? { maxHeight: COLLAPSED_MAX_HEIGHT_PX } : undefined}
      >
        {children}
      </div>
      {isLong && (
        <button type="button" className="collapsible-text-toggle" aria-expanded={expanded} onClick={toggle}>
          <span>{expanded ? 'Show less' : 'Show more'}</span>
          <i className={`fas fa-chevron-${expanded ? 'up' : 'down'}`} aria-hidden="true" />
        </button>
      )}
    </div>
  );
};

export default CollapsibleText;
